import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { GitMerge, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConfirmDialog, EmptyState, Field, PageHeader, Panel, RowsSkeleton, Tag } from "@/components/app/kit";
import { Attachments, MedStatus, PatientPicker } from "@/components/med/shared";
import { useFetch } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/i18n";

const MED = [["medical_history", "Antécédents médicaux"], ["surgical_history", "Antécédents chirurgicaux"], ["family_history", "Antécédents familiaux"], ["treatments", "Traitements en cours"]];

const Medical = ({ p, reload }) => {
  const { can } = useAuth();
  const [v, setV] = useState({ ...p, allergies_text: (p.allergies || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/t/patients/${p.id}/medical`, { ...Object.fromEntries(MED.map(([k]) => [k, v[k] || ""])), blood_group: v.blood_group || "",
        allergies: v.allergies_text.split(",").map((x) => x.trim()).filter(Boolean), version: p.version });
      toast.success("Dossier médical mis à jour");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const ro = !can("records:write");
  return (
    <Panel title="Dossier médical" testid="medical-panel">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Allergies (séparées par des virgules)"><Input value={v.allergies_text} disabled={ro} onChange={(e) => setV({ ...v, allergies_text: e.target.value })} data-testid="medical-allergies" /></Field>
        <Field label="Groupe sanguin"><Input value={v.blood_group || ""} disabled={ro} onChange={(e) => setV({ ...v, blood_group: e.target.value })} data-testid="medical-blood" /></Field>
        {MED.map(([k, l]) => <Field key={k} label={l}><Textarea rows={4} value={v[k] || ""} disabled={ro} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`medical-${k}`} /></Field>)}
      </div>
      <div className="mt-4"><Attachments entityType="patient" entityId={p.id} canWrite={!ro} /></div>
      {!ro && <Button className="mt-5" onClick={save} disabled={busy} data-testid="medical-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Enregistrer</Button>}
    </Panel>
  );
};

const LINKS = { consultations: ["Consultations", "/cabinet/consultations/"], prescriptions: ["Ordonnances", "/cabinet/ordonnances?id="], exams: ["Examens", "/cabinet/examens?id="],
  documents: ["Documents", "/cabinet/documents?id="], appointments: ["Rendez-vous", null] };

const Timeline = ({ pid }) => {
  const { data } = useFetch(`/t/patients/${pid}/timeline`);
  if (!data) return <RowsSkeleton />;
  return (
    <div className="grid md:grid-cols-2 gap-5">
      {Object.entries(data).map(([k, rows]) => (
        <Panel key={k} title={`${LINKS[k][0]} (${rows.length})`} testid={`timeline-${k}`}>
          {rows.length === 0 ? <p className="text-sm text-slate-500">Aucun élément.</p> : rows.slice(0, 15).map((r) => (
            <div key={r.id} className="flex items-center gap-3 py-1.5 text-sm border-b border-slate-100 last:border-0">
              <span className="text-xs text-slate-400 w-24">{(r.date || r.start || "").slice(0, 10) || fmtDate(r.created_at)}</span>
              {LINKS[k][1] ? <Link className="flex-1 hover:text-sky-700 truncate" to={LINKS[k][1] + r.id}>{r.reason || r.type || r.title || (r.items || []).map((i) => i.drug).join(", ")}</Link> : <span className="flex-1 truncate">{r.reason}</span>}
              <MedStatus s={r.status} />
            </div>
          ))}
        </Panel>
      ))}
    </div>
  );
};

const WORDS = { cree: "création", creee: "création", modifie: "modification", modifiee: "modification", supprime: "suppression", annule: "annulation", valide: "validation",
  finalise: "finalisation", rendez_vous: "rendez-vous", debut: "début", fin: "fin", piece_jointe: "pièce jointe", corrige: "correction" };
const human = (s) => (s || "").split(".").map((w) => WORDS[w] || w.replace(/_/g, " ")).join(" ");

const History = ({ pid }) => {
  const { data } = useFetch(`/t/patients/${pid}/history`);
  if (!data) return <RowsSkeleton />;
  return (
    <Panel title="Historique des modifications" testid="patient-history">
      {data.length === 0 ? <p className="text-sm text-slate-500">Aucune modification.</p> : data.map((h) => (
        <div key={h.id} className="py-2 border-b border-slate-100 last:border-0 text-sm">
          <span className="text-xs text-slate-400 mr-3">{fmtDateTime(h.at)}</span><Tag tone="gray">{human(h.entity)} · {human(h.action)}</Tag><span className="ml-2 text-slate-600">{h.actor_email}</span>
          {Object.keys(h.changes || {}).length > 0 && <span className="text-xs text-slate-500 ml-2">champs modifiés : {Object.keys(h.changes).map(human).join(", ")}</span>}
          {h.reason && <span className="text-xs italic text-slate-500 ml-2">« {h.reason} »</span>}
        </div>
      ))}
    </Panel>
  );
};

export default function PatientRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { data: p, error, reload } = useFetch(`/t/patients/${id}`);
  const [merge, setMerge] = useState(false);
  const [m, setM] = useState({ source_id: null, reason: "" });
  useEffect(() => { setMerge(false); }, [id]);
  if (error) return <EmptyState title="Dossier introuvable" text={error} />;
  if (!p) return <RowsSkeleton rows={6} />;
  const doMerge = async () => {
    try {
      await api.post(`/t/patients/${id}/merge`, m);
      toast.success("Dossiers fusionnés");
      setMerge(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const dash = (v) => (v && String(v).trim()) || "—";
  const info = [["N° de dossier", p.file_number], ["Naissance", fmtDate(p.birth_date)], ["Sexe", { F: "Féminin", M: "Masculin", X: "Autre" }[p.sex] || "—"], ["Téléphone", dash(p.phone)], ["E-mail", dash(p.email)],
    ["Adresse", dash(`${p.address || ""} ${p.city || ""}`)], ["Assurance", dash(`${p.insurance || ""} ${p.insurance_number || ""}`)],
    ["Personne à contacter", dash(p.contact_person?.name && `${p.contact_person.name} (${p.contact_person.relation}) ${p.contact_person.phone}`)]];
  return (
    <div>
      <PageHeader eyebrow={`Dossier ${p.file_number}`} title={<span className="flex items-center gap-3 flex-wrap">{p.last_name.toUpperCase()} {p.first_name}{p.status === "merged" && <Tag tone="gray">Fusionné</Tag>}{(p.allergies || []).length > 0 && <Tag tone="red" data-testid="allergy-badge">Allergies : {p.allergies.join(", ")}</Tag>}</span>}
        actions={<>
          <Button variant="outline" onClick={() => navigate("/cabinet/patients")}>Retour</Button>
          {can("patients:merge") && p.status !== "merged" && <Button variant="outline" onClick={() => setMerge(true)} data-testid="patient-merge-button"><GitMerge className="h-4 w-4 mr-2" />Fusionner un doublon</Button>}
        </>} />
      <Tabs defaultValue="identity">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="identity" data-testid="record-tab-identity">Identité</TabsTrigger>
          <TabsTrigger value="medical" data-testid="record-tab-medical">Dossier médical</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="record-tab-timeline">Parcours de soins</TabsTrigger>
          <TabsTrigger value="history" data-testid="record-tab-history">Historique</TabsTrigger>
        </TabsList>
        <TabsContent value="identity">
          <Panel title="Identité et informations administratives"><div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {info.map(([l, v]) => <div key={l}><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{l}</p><p className="text-sm mt-1">{v || "—"}</p></div>)}
          </div>{p.notes && <p className="text-sm text-slate-600 mt-5 whitespace-pre-line">{p.notes}</p>}</Panel>
        </TabsContent>
        <TabsContent value="medical">{can("records:read") ? <Medical p={p} reload={reload} /> : <EmptyState icon={Lock} testid="medical-restricted" title="Accès restreint" text="Votre rôle ne permet pas de consulter les informations médicales." />}</TabsContent>
        <TabsContent value="timeline"><Timeline pid={id} /></TabsContent>
        <TabsContent value="history"><History pid={id} /></TabsContent>
      </Tabs>
      <ConfirmDialog open={merge} onOpenChange={setMerge} destructive onConfirm={doMerge} confirmLabel="Fusionner" title="Fusionner un dossier en doublon"
        description={`Toutes les données du dossier sélectionné seront rattachées à ${p.file_number}. Le dossier source est conservé, marqué « fusionné ». Opération journalisée.`}>
        <div className="space-y-3"><PatientPicker value={m.source_id} onChange={(v) => setM({ ...m, source_id: v })} testid="merge-source" />
          <Field label="Motif (obligatoire)"><Input value={m.reason} onChange={(e) => setM({ ...m, reason: e.target.value })} data-testid="merge-reason" /></Field></div>
      </ConfirmDialog>
    </div>
  );
}
